import { deepMerge } from '@mantine/core';
import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { Project } from '../config/projects';
import { checkAuthorized } from '../utils/check-authorized';

type User = {
  name: string;
  email: string;
};

type Session = {
  token: string;
  isAuthorized: boolean | undefined;
  user: User;
};

export type GlobalStore = {
  activeProject: Project;
  sessions: Record<Project, Session>;
  setActiveProject(project: Project): void;
  setToken(project: Project, token: string): void;
  setUserData(project: Project, data: User): void;
  checkAuthorized(project: Project): void;
  logout(project: Project): void;
  navigation: {
    isDirty: boolean;
    setDirty(isDirty: boolean): void;
  };
  localization: {
    currentLanguage: 'ru' | 'en';
    setCurrentLanguage(language: 'ru' | 'en'): void;
  };
};

const emptySession = (): Session => ({
  token: '',
  isAuthorized: undefined,
  user: {
    name: '',
    email: '',
  },
});

type LegacyStore = {
  auth?: {
    token?: string;
    isAuthorized: boolean | undefined;
    user?: User;
  };
};

export const migratePersistedGlobalStore = (persistedState: unknown) => {
  const persisted = persistedState as LegacyStore & Partial<GlobalStore>;
  if (persisted.sessions) {
    return persisted as GlobalStore;
  }

  const hatfa = emptySession();
  if (persisted.auth) {
    hatfa.token = persisted.auth.token || '';
    hatfa.isAuthorized = persisted.auth.isAuthorized;
    hatfa.user = persisted.auth.user || hatfa.user;
  }

  return {
    ...persisted,
    activeProject: 'hatfa',
    sessions: {
      hatfa,
      kitaba: emptySession(),
    },
  } as GlobalStore;
};

export const useGlobalStore = create(
  subscribeWithSelector(
    persist(
      immer<GlobalStore>((set) => ({
        activeProject: 'hatfa',
        sessions: {
          hatfa: emptySession(),
          kitaba: emptySession(),
        },
        setActiveProject: (project) => {
          set((state) => {
            state.activeProject = project;
          });
        },
        setToken: (project, token) => {
          set((state) => {
            state.sessions[project].token = token;
            state.sessions[project].isAuthorized = true;
          });
        },
        checkAuthorized: (project) => {
          set((state) => {
            state.sessions[project].isAuthorized = checkAuthorized(
              state.sessions[project].token,
            );
          });
        },
        setUserData: (project, data) => {
          set((state) => {
            state.sessions[project].user = data;
          });
        },
        logout: (project) => {
          set((state) => {
            state.sessions[project].token = '';
            state.sessions[project].isAuthorized = false;
          });
        },
        navigation: {
          isDirty: false,
          setDirty: (isDirty) => {
            set((state) => {
              state.navigation.isDirty = isDirty;
            });
          },
        },
        localization: {
          currentLanguage: 'ru',
          setCurrentLanguage(language) {
            set((state) => {
              state.localization.currentLanguage = language;
            });
            location.reload();
          },
        },
      })),
      {
        name: 'auth',
        version: 2,
        migrate: migratePersistedGlobalStore,
        merge: (persistedState, currentState) => {
          return deepMerge(currentState, persistedState);
        },
      },
    ),
  ),
);
